export const ROUND_COUNT = 10;
export const STORAGE_PREFIX = "mrb_quotes_best_";
export const STREAK_PREFIX = "mrb_quotes_streak_";
export const STARS_PREFIX = "mrb_quotes_stars_";

function read(key: string): number {
  if (typeof window === "undefined") return 0;
  return Number(localStorage.getItem(key) || 0);
}
function write(key: string, value: number) {
  if (typeof window === "undefined") return;
  localStorage.setItem(key, String(value));
}

export function loadBest(game: string): number {
  // Migration: old "guess-author" → new "who-said-this"
  if (game === "who-said-this") {
    const legacy = read(STORAGE_PREFIX + "guess-author");
    const current = read(STORAGE_PREFIX + game);
    return Math.max(legacy, current);
  }
  return read(STORAGE_PREFIX + game);
}

export function saveBest(game: string, score: number): number {
  const prev = loadBest(game);
  if (score > prev) {
    write(STORAGE_PREFIX + game, score);
    return score;
  }
  return prev;
}

export function loadStreak(game: string): number {
  return read(STREAK_PREFIX + game);
}
export function saveStreak(game: string, streak: number): number {
  const prev = loadStreak(game);
  if (streak > prev) {
    write(STREAK_PREFIX + game, streak);
    return streak;
  }
  return prev;
}

/** Returns the stars (0..3) achieved on a given scramble level. */
export function loadStars(level: number): number {
  return read(STARS_PREFIX + "scramble_lvl" + level);
}
export function saveStars(level: number, stars: number): number {
  const prev = loadStars(level);
  if (stars > prev) {
    write(STARS_PREFIX + "scramble_lvl" + level, stars);
    return stars;
  }
  return prev;
}

/** Streak multiplier — used by trivia games. */
export function streakMultiplier(streak: number): number {
  if (streak >= 10) return 4;
  if (streak >= 5) return 3;
  if (streak >= 3) return 2;
  return 1;
}

export const ANSWER_LETTERS = ["A", "B", "C", "D"] as const;

export const SCRAMBLE_LEVELS = [
  { id: 1, name: "Level I", subtitle: "Short epigrams", min: 0, max: 6, timeLimit: 45 },
  { id: 2, name: "Level II", subtitle: "Compact wisdom", min: 7, max: 9, timeLimit: 45 },
  { id: 3, name: "Level III", subtitle: "Full sentences", min: 10, max: 13, timeLimit: 60 },
  { id: 4, name: "Level IV", subtitle: "Long-form", min: 14, max: 18, timeLimit: 75 },
  { id: 5, name: "Level V", subtitle: "Master prose", min: 19, max: 200, timeLimit: 90 },
] as const;

export type ScrambleLevel = (typeof SCRAMBLE_LEVELS)[number];

/** Word Bloom — highest level the player has opened on this device. */
export const BLOOM_LEVEL_KEY = "mrb_quotes_bloom_level";

export function loadBloomLevel(): number {
  return Math.max(1, read(BLOOM_LEVEL_KEY));
}
export function saveBloomLevel(level: number): number {
  const prev = loadBloomLevel();
  if (level > prev) {
    write(BLOOM_LEVEL_KEY, level);
    return level;
  }
  return prev;
}

export const WORDLE_MAX_GUESSES = 6;
export const WORDLE_ROUNDS_PER_PACK = 5;

/** Build a Twitter / WhatsApp-friendly share text for any game result. */
export function buildShareText(opts: {
  gameName: string;
  scoreLine: string;
  emojiGrid?: string;
  url?: string;
  extra?: string;
}): string {
  const url = opts.url ?? (typeof window !== "undefined" ? window.location.origin : "");
  return [
    `Mr. B Quotes · ${opts.gameName}`,
    opts.scoreLine,
    opts.emojiGrid ?? "",
    opts.extra ?? "",
    url,
  ].filter(Boolean).join("\n");
}
